import {
  hasPermissionInMap,
  mergeRolePermissions,
} from '@/__core/permissions/utils/permissionResolver';

export interface CompanyData {
  company_id: string;
  company_name: string;
  company_code: string;
  company_type: string;
  roles: {
    role_id: string;
    role_name: string;
    role_display_name: string;
    role_level: string;
    permissions: Record<string, string[]>;
    is_primary: boolean;
    created_at: string;
  }[];
}

export interface SelectedCompany {
  company_id: string;
  company_name: string;
  company_code: string;
  company_type: string;
  roles: {
    role_id: string;
    role_name: string;
    role_display_name: string;
    role_level: string;
    permissions: Record<string, string[]>;
    is_primary: boolean;
    created_at: string;
  }[];
  selected_at: string;
}

const COMPANY_STORAGE_KEY = 'selected_company';
const REMEMBER_COMPANY_KEY = 'remember_company';

/**
 * Lưu thông tin công ty đã chọn vào storage
 */
export const saveSelectedCompany = (
  company: CompanyData,
  remember: boolean = false
): void => {
  const selectedCompany: SelectedCompany = {
    company_id: company.company_id,
    company_name: company.company_name,
    company_code: company.company_code,
    company_type: company.company_type,
    roles: company.roles.map(role => ({
      role_id: role.role_id,
      role_name: role.role_name,
      role_display_name: role.role_display_name,
      role_level: role.role_level || 'company',
      permissions: role.permissions || {},
      is_primary: role.is_primary,
      created_at: role.created_at
    })),
    selected_at: new Date().toISOString()
  };

  // Always mirror selected company in localStorage as cross-tab fallback.
  // The remember flag still controls preferred token/session behavior.
  localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));

  if (remember) {
    localStorage.setItem(REMEMBER_COMPANY_KEY, 'true');
    sessionStorage.removeItem(REMEMBER_COMPANY_KEY);
  } else {
    sessionStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
    localStorage.setItem(REMEMBER_COMPANY_KEY, 'false');
    sessionStorage.setItem(REMEMBER_COMPANY_KEY, 'false');
  }
};

/**
 * Lấy thông tin công ty đã chọn từ storage
 */
export const getSelectedCompany = (): SelectedCompany | null => {
  try {
    // Read preferred storage by remember flag, then fallback to the other store.
    const rememberCompany = localStorage.getItem(REMEMBER_COMPANY_KEY);
    const primaryStorage = rememberCompany === 'true' ? localStorage : sessionStorage;
    const fallbackStorage = primaryStorage === localStorage ? sessionStorage : localStorage;
    
    let storedCompany = primaryStorage.getItem(COMPANY_STORAGE_KEY);
    if (!storedCompany) {
      storedCompany = fallbackStorage.getItem(COMPANY_STORAGE_KEY);
    }

    if (storedCompany) {
      return JSON.parse(storedCompany);
    }
    
    return null;
  } catch (error) {
    console.error('Error getting selected company:', error);
    return null;
  }
};

/**
 * Xóa thông tin công ty đã chọn khỏi storage
 */
export const clearSelectedCompany = (): void => {
  localStorage.removeItem(COMPANY_STORAGE_KEY);
  localStorage.removeItem(REMEMBER_COMPANY_KEY);
  sessionStorage.removeItem(COMPANY_STORAGE_KEY);
  sessionStorage.removeItem(REMEMBER_COMPANY_KEY);
};

/**
 * Kiểm tra xem có công ty nào được lưu trong storage không
 */
export const hasSelectedCompany = (): boolean => {
  const rememberCompany = localStorage.getItem(REMEMBER_COMPANY_KEY);
  const primaryStorage = rememberCompany === 'true' ? localStorage : sessionStorage;
  const fallbackStorage = primaryStorage === localStorage ? sessionStorage : localStorage;

  return (
    primaryStorage.getItem(COMPANY_STORAGE_KEY) !== null ||
    fallbackStorage.getItem(COMPANY_STORAGE_KEY) !== null
  );
};

/**
 * Lấy company ID hiện tại
 */
export const getCurrentCompanyId = (): string | null => {
  const selectedCompany = getSelectedCompany();
  return selectedCompany?.company_id || null;
};

/**
 * Lấy company name hiện tại
 */
export const getCurrentCompanyName = (): string | null => {
  const selectedCompany = getSelectedCompany();
  return selectedCompany?.company_name || null;
};

/**
 * Lấy roles hiện tại
 */
export const getCurrentRoles = (): SelectedCompany['roles'] => {
  const selectedCompany = getSelectedCompany();
  return selectedCompany?.roles || [];
};

/**
 * Lấy permissions hiện tại (tổng hợp từ tất cả roles)
 */
export const getCurrentPermissions = (): Record<string, string[]> => {
  const roles = getCurrentRoles();
  return mergeRolePermissions(roles);
};

/**
 * Kiểm tra permission cho resource và action
 */
export const hasPermission = (resource: string, action: string): boolean => {
  const permissions = getCurrentPermissions();
  return hasPermissionInMap(permissions, resource, action);
};

/**
 * Lấy tất cả resources có permissions
 */
export const getAvailableResources = (): string[] => {
  const permissions = getCurrentPermissions();
  return Object.keys(permissions);
};

/**
 * Lấy tất cả actions cho một resource
 */
export const getAvailableActions = (resource: string): string[] => {
  const permissions = getCurrentPermissions();
  return permissions[resource] || [];
};

/**
 * Kiểm tra xem công ty có được lưu với remember = true không
 */
export const isCompanyRemembered = (): boolean => {
  return localStorage.getItem(REMEMBER_COMPANY_KEY) === 'true';
};

/**
 * Cập nhật thông tin công ty (khi có thay đổi từ server)
 */
export const updateSelectedCompany = (updatedCompany: Partial<CompanyData>): void => {
  const currentCompany = getSelectedCompany();
  if (!currentCompany) return;

  const rememberCompany = localStorage.getItem(REMEMBER_COMPANY_KEY);
  const preferredStorage = rememberCompany === 'true' ? localStorage : sessionStorage;
  
  const updatedSelectedCompany: SelectedCompany = {
    ...currentCompany,
    company_name: updatedCompany.company_name || currentCompany.company_name,
    company_code: updatedCompany.company_code || currentCompany.company_code,
    company_type: updatedCompany.company_type || currentCompany.company_type,
    selected_at: new Date().toISOString()
  };

  // Keep local copy synced for cross-tab behavior.
  localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(updatedSelectedCompany));
  preferredStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(updatedSelectedCompany));
};
